/**
 * Leichte Offer-Karten vor dem Monolith-Boot (sichtbarer Inhalt in ~1–2s).
 * Monolith ersetzt spaeter via renderOfferCards — Shell erkennt data-vivlico-early-offers.
 */
import type { DisplayRoute } from './urlParse.js';
import type { EarlyAppointment } from './dataLoader.js';
import { computeOfferMasonryMetrics } from './offerMasonryLayout.js';

const BUNNY = 'vivlico';

function escapeHtml(raw: string): string {
    return String(raw || '')
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;');
}

function imageHtml(courseImage: unknown): string {
    if (courseImage == null || typeof courseImage === 'object') {
        return '<span class="registrationImageFallback registrationImageFallback--course" aria-hidden="true"><i class="fas fa-calendar-alt"></i></span>';
    }
    const path = String(courseImage).trim();
    if (!path) {
        return '<span class="registrationImageFallback registrationImageFallback--course" aria-hidden="true"><i class="fas fa-calendar-alt"></i></span>';
    }
    const src = `https://${BUNNY}.b-cdn.net/${escapeHtml(path)}`;
    return `<img src="${src}" loading="eager" decoding="async" onerror="this.remove()"><span class="registrationImageFallback registrationImageFallback--course" aria-hidden="true"><i class="fas fa-calendar-alt"></i></span>`;
}

function formatPrize(reg: Record<string, unknown> | undefined): string {
    if (!reg) return '';
    const prize = reg.prize ?? reg.price;
    if (prize == null || prize === '') return '';
    const n = Number(prize);
    if (!Number.isFinite(n)) return '';
    try {
        return `<p class="prize">${new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' }).format(n)}</p>`;
    } catch {
        return `<p class="prize">${escapeHtml(String(n))} €</p>`;
    }
}

function dateRegionLine(appt: EarlyAppointment): string {
    const reg = appt.registration && typeof appt.registration === 'object'
        ? appt.registration as Record<string, unknown>
        : {};
    const dateLabel = reg.dateString != null ? String(reg.dateString)
        : (appt.dateString != null ? String(appt.dateString) : '');
    const region = appt.region != null ? String(appt.region) : '';
    const parts = [dateLabel, region].filter((p) => p.trim() !== '');
    return parts.length ? escapeHtml(parts.join(' || ')) : '';
}

export function buildEarlyCardHtml(appt: EarlyAppointment): string {
    const title = escapeHtml(String(appt.title || 'Angebot'));
    const reg = appt.registration && typeof appt.registration === 'object'
        ? appt.registration as Record<string, unknown>
        : undefined;
    const intro = reg && reg.registrationIntroText != null
        ? escapeHtml(String(reg.registrationIntroText))
        : '';
    const dateLine = dateRegionLine(appt);
    return (
        `<div class="outerCardContainer" data-vivlico-early-card="1" aria-busy="true">`
        + `<div class="innerCardContainer">`
        + `<div class="textContainer">`
        + `<h5>${title}</h5>`
        + (dateLine ? `<p class="date">${dateLine}</p>` : '')
        + (intro ? `<p class="intro">${intro}</p>` : '')
        + formatPrize(reg)
        + `</div>`
        + `<div class="imageContainer">${imageHtml(appt.courseImage)}</div>`
        + `</div></div>`
    );
}

/** Variants where a card / single early-paint is useful. */
export function supportsEarlyCardGrid(route: DisplayRoute): boolean {
    const v = route.variant || 'offer';
    if (route.isSingleCourse) {
        // singleDirect opens the form immediately — skip card chrome.
        return v !== 'singleDirect' && v !== 'bookingDirect';
    }
    return v === '' || v === 'offer' || v === 'courseCategory' || v === 'listView' || v === 'member';
}

/** Pure HTML for early cards (no DOM) — used by render + unit tests. */
export function buildEarlyOfferCardsHtml(appointments: EarlyAppointment[]): string {
    return appointments.slice(0, 24).map(buildEarlyCardHtml).join('');
}

/**
 * Ersetzt Skeleton durch echte (nicht-interaktive) Karten.
 * Klick erst nach Monolith (renderOfferCards setzt onclick).
 */
export function renderEarlyOfferCards(route: DisplayRoute, appointments: EarlyAppointment[]): boolean {
    if (typeof document === 'undefined' || !appointments.length) return false;
    if (!supportsEarlyCardGrid(route)) return false;
    if ((globalThis as { __vivMonolithBooted?: boolean }).__vivMonolithBooted === true) return false;

    const main = document.getElementById('mainContent');
    if (!main) return false;

    const cardsHtml = buildEarlyOfferCardsHtml(appointments);
    if (!cardsHtml) return false;

    let container = document.getElementById('offerContainer');
    if (!container) {
        container = document.createElement('div');
        container.className = 'offerContainer';
        container.id = 'offerContainer';
        main.appendChild(container);
    }

    container.setAttribute('data-vivlico-early-offers', '1');
    if (route.isSingleCourse) {
        container.setAttribute('data-vivlico-early-single', '1');
    }
    container.classList.remove('offerMasonry--pending');
    container.classList.add('offerMasonry', 'offerMasonry--ready', 'offerMasonry--revealed');
    container.innerHTML = cardsHtml;

    // Lightweight absolute layout (same CSS as monolith masonry) so cards are visible.
    layoutEarlyMasonry(container);

    (globalThis as { __vivEarlyOffersPainted?: boolean }).__vivEarlyOffersPainted = true;
    return true;
}

function layoutEarlyMasonry(container: HTMLElement): void {
    const gap = 27;
    const minCol = 270;
    const width = container.clientWidth || container.getBoundingClientRect().width || window.innerWidth || 0;
    if (width <= 0) return;
    const cards = Array.from(container.querySelectorAll(':scope > .outerCardContainer')) as HTMLElement[];
    if (!cards.length) return;
    const metrics = computeOfferMasonryMetrics(width, gap, minCol, cards.length);
    container.style.setProperty('--offer-masonry-col-width', `${metrics.colWidth}px`);
    const colHeights = new Array(metrics.packCols).fill(0);
    cards.forEach((card) => {
        card.style.width = `${metrics.colWidth}px`;
        let col = 0;
        for (let i = 1; i < metrics.packCols; i++) {
            if (colHeights[i] < colHeights[col]) col = i;
        }
        const x = metrics.offsetX + col * (metrics.colWidth + metrics.gap);
        const y = colHeights[col];
        card.style.transform = `translate(${x}px, ${y}px)`;
        const h = Math.max(1, Math.ceil(card.getBoundingClientRect().height || card.offsetHeight || 200));
        colHeights[col] = y + h + metrics.gap;
    });
    const maxH = Math.max.apply(null, colHeights);
    container.style.height = `${Math.max(0, Math.ceil(maxH - metrics.gap))}px`;
}
